import React from "react";
import { money, DateFormat } from "../../utils/helpers";

function OperationalExpenseTable({ rows, total }) {
  return (
    <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
      <h3 className="mb-3 text-sm font-semibold text-gray-700">
        Operational Expense Recap
      </h3>

      <table className="w-full text-xs">
        <thead>
          <tr className="text-left text-gray-400">
            <th className="pb-2">Category</th>
            <th className="pb-2">Description</th>
            <th className="pb-2">Date</th>
            <th className="pb-2">Method</th>
            <th className="pb-2 text-right">Amount</th>
          </tr>
        </thead>

        <tbody className="divide-y divide-gray-50">
          {rows?.map((row, i) => (
            <tr key={i}>
              <td className="py-1.5 text-gray-700">{row.category}</td>
              <td className="py-1.5 text-gray-500">{row.description}</td>
              <td className="py-1.5 text-gray-500">
                {DateFormat(row.date)}
              </td>
              <td className="py-1.5 text-gray-500">
                {row.paymentMethod}
              </td>
              <td className="py-1.5 text-right font-medium text-gray-900">
                {money(row.amount)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="mt-3 flex justify-between border-t border-gray-100 pt-2 text-sm font-semibold text-gray-800">
        <span>Total Operational Expense</span>
        <span>{money(total)}</span>
      </div>
    </div>
  );
}

function ProfitLossCard({
  revenue,
  cogs,
  operationalExpense,
  netProfit,
  periodLabel,
}) {
  const isLoss = netProfit < 0;

  return (
    <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-700">
          Profit &amp; Loss Report
        </h3>

        <span className="text-xs text-gray-400">
          Period: {periodLabel}
        </span>
      </div>

      <div className="grid grid-cols-3 gap-4 text-xs">
        {/* Revenue */}
        <div>
          <div className="mb-1 font-medium text-gray-600">
            Revenue
          </div>

          <div className="flex justify-between text-gray-500">
            <span>Product Sales</span>
            <span>{money(revenue?.productSales)}</span>
          </div>

          <div className="flex justify-between text-gray-500">
            <span>Racket Rental</span>
            <span>{money(revenue?.racketRental)}</span>
          </div>

          <div className="flex justify-between text-gray-500">
            <span>Fees &amp; Other</span>
            <span>{money(revenue?.feesAndOther)}</span>
          </div>

          <div className="mt-1 flex justify-between border-t border-gray-100 pt-1 font-semibold text-emerald-600">
            <span>Total Omzet</span>
            <span>{money(revenue?.totalRevenue)}</span>
          </div>
        </div>

        {/* COGS */}
        <div>
          <div className="mb-1 font-medium text-gray-600">
            Cost of Goods Sold (COGS)
          </div>

          <div className="flex justify-between text-gray-500">
            <span>Purchases</span>
            <span>{money(cogs?.purchases)}</span>
          </div>

          <div className="flex justify-between text-gray-500">
            <span>COGS Sold</span>
            <span>{money(cogs?.cogsSold)}</span>
          </div>

          <div className="mt-1 flex justify-between border-t border-gray-100 pt-1 font-semibold text-red-500">
            <span>Total COGS</span>
            <span>{money(cogs?.totalCogs)}</span>
          </div>
        </div>

        {/* Operational Expense */}
        <div>
          <div className="mb-1 font-medium text-gray-600">
            Operational Expense
          </div>

          <div className="flex justify-between text-gray-500">
            <span>Staff Salary</span>
            <span>{money(operationalExpense?.staffSalary)}</span>
          </div>

          <div className="flex justify-between text-gray-500">
            <span>Marketing</span>
            <span>{money(operationalExpense?.marketing)}</span>
          </div>

          <div className="flex justify-between text-gray-500">
            <span>Utilities &amp; Internet</span>
            <span>{money(operationalExpense?.utilitiesInternet)}</span>
          </div>

          <div className="flex justify-between text-gray-500">
            <span>Maintenance</span>
            <span>{money(operationalExpense?.maintenanceCare)}</span>
          </div>

          <div className="flex justify-between text-gray-500">
            <span>Supplies</span>
            <span>{money(operationalExpense?.operationalSupplies)}</span>
          </div>

          <div className="flex justify-between text-gray-500">
            <span>Other</span>
            <span>{money(operationalExpense?.other)}</span>
          </div>

          <div className="mt-1 flex justify-between border-t border-gray-100 pt-1 font-semibold text-red-500">
            <span>Total Expense</span>
            <span>
              {money(
                operationalExpense?.totalOperationalExpense
              )}
            </span>
          </div>
        </div>
      </div>

      <div
        className={`mt-4 flex items-center justify-between rounded-lg px-4 py-3 text-sm font-semibold ${
          isLoss
            ? "bg-red-50 text-red-600"
            : "bg-emerald-50 text-emerald-600"
        }`}
      >
        <span>NET PROFIT</span>
        <span>{money(netProfit)}</span>
      </div>
    </div>
  );
}

export default function FinanceSection({
  data,
  loading,
  error,
}) {
  if (error) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-600">
        {error}
      </div>
    );
  }

  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {Array.from({ length: 2 }).map((_, i) => (
          <div
            key={i}
            className="h-72 animate-pulse rounded-xl bg-gray-100"
          />
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <OperationalExpenseTable
        rows={data?.operationalExpenseRecap}
        total={data?.totalOperationalExpense}
      />

      <ProfitLossCard
        revenue={data?.revenue}
        cogs={data?.cogs}
        operationalExpense={data?.operationalExpense}
        netProfit={data?.netProfit}
        periodLabel={data?.periodLabel}
      />
    </div>
  );
}